import { Assert } from "../Assert";
import { Configuration } from "../Configuration";
import { PartialNullable, RadPromiseLike } from "../Extensions";
import { ObjectHelper } from "../Helpers/ObjectHelper";
import { PathHelper } from "../Helpers/PathHelper";
import { IPromisePublic, PromiseHelper } from "../Helpers/PromiseHelper";
import { StringHelper } from "../Helpers/StringHelper";
import { $$, IJNativeEvent, JNativeStatic } from "../JNative";
import { PermissionCache } from "../PermissionCache";
import { PermissionCacheService } from "../PermissionCacheService";
import { PropertyChange } from "../PropertyChange";
import { IRadObject, Property, PropertyBoolean, PropertyString } from "../RadObject";
import { ApplicationInformationService, WindowShim } from "../RadWindow";
import { AjaxRequest } from "../Server/AjaxRequest";
import { WebApiClient } from "../Server/WebApiClient";
import { WebApiClientService } from "../Server/WebApiClientService";
import { WebClient } from "../Server/WebClient";
import { WebClientService } from "../Server/WebClientService";
import { ServiceManager } from "../ServiceManager";
import { ApplicationData } from "./ApplicationData";
import { ApplicationDataService } from "./ApplicationDataService";
import { ApplicationService } from "./ApplicationService";
import { Accordion } from "./Controls/Accordion";
import { AccordionPanel } from "./Controls/AccordionPanel";
import { AjaxResponseMessageDialog } from "./Controls/AjaxResponseMessageDialog";
import { Button } from "./Controls/Button";
import { Control, ControlPropertyValues } from "./Controls/Control";
import { ControlRef } from "./Controls/ControlRef";
import { Dialog } from "./Controls/Dialog";
import { MainMenu } from "./Controls/MainMenu";
import { Menu, MenuItem } from "./Controls/Menu";
import { PageDimmer } from "./Controls/PageDimmer";
import { Panel } from "./Controls/Panel";
import { Toolbar } from "./Controls/Toolbar";
import { ToolbarButton } from "./Controls/ToolbarButton";
import { ToolbarImage } from "./Controls/ToolbarImage";
import { ToolbarSplitter } from "./Controls/ToolbarSplitter";
import { UserAccountDialog } from "./Controls/UserAccountDialog";
import { WindowHeader } from "./Controls/WindowHeader";
import { DialogResultEnum } from "./Dialogs/DialogResultEnum";
import { JsxFragment } from "./JsxFragment";
import { NAMESPACE } from "./_Namespace";
import { PageManager } from "./PageManager";
import { IPage } from "./Pages/Page";

import "./Application.scss";

const
    radWindow = WindowShim();

(() =>
{
    let
        deviceReady = false,
        documentReady = false;

    function checkReady()
    {
        if (deviceReady && documentReady)
        {
            setTimeout(() =>
            {
                $$(document).trigger("launchapplication");
            });
        }
    }

    $$(() => { documentReady = true; checkReady(); });
    $$(document).on("deviceready", () => { deviceReady = true; checkReady(); });

})();

if (!radWindow.cordova)
{
    $$(() =>
    {
        setTimeout(() =>
        {
            $$(document).trigger("deviceready");
        });
    });
}

export type ApplicationPropertyValues =
    ControlPropertyValues
    & {
        authenticationScheme: string;
        configuration: Configuration;
        firstMenuItemIsHomePage: boolean,
        inhibitNetworkErrors: boolean;
        loginWithDialog: boolean;
        supportDesktopLayout: boolean;
    };

export type ApplicationProperties = {
    [name in keyof ApplicationPropertyValues]: Property<ApplicationPropertyValues[name]>
};

export interface IApplication extends IRadObject, ApplicationProperties
{
    getPlatform(): string;
    logoutAsync(): Promise<void>;
}

export class Application<TPropertyValues extends ApplicationPropertyValues = ApplicationPropertyValues> extends Control<TPropertyValues> implements ApplicationProperties
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Application";
    public static override className = "Application";

    // #region Global Variables

    private static instance?: Application;

    // #endregion

    // #region Constructor

    static {

        $$(document).on("click", "a.rad-action-main-menu-open", (evt: IJNativeEvent) =>
        {
            evt.preventDefault();
            evt.stopPropagation();

            this.instance?._mainMenuControlRef.current?.open();
        });
        $$(document).on("click", "a.rad-action-main-menu-close, .rad-control-main-menu a", (evt: IJNativeEvent) =>
        {
            const
                target = (evt.currentTarget as any).getAttribute("target");

            if (target && target !== "_self")
                return;

            evt.preventDefault();
            evt.stopPropagation();

            this.instance?._mainMenuControlRef.current?.close();
        });

    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ApplicationPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                authenticationScheme: "Cookies",
                loginWithDialog: true,
                supportDesktopLayout: true,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _mainMenuControlRef!: ControlRef<MainMenu>;
    protected _panelDialogContainerControlRef!: ControlRef<Panel>;
    protected _panelPageContainerControlRef!: ControlRef<Panel>;

    protected _onAuthenticatedAsyncPromise?: IPromisePublic;
    protected checkLoginTimeoutId?: number;
    protected initialized?: boolean;
    protected loadedInitialPage: boolean;
    protected onAuthenticatedAsyncCalled: boolean;
    public permissionCache?: PermissionCache;
    protected triggeredReady: boolean;
    protected userAccountDialog?: Dialog;
    protected userMenu?: Menu;
    public webClient?: WebClient;
    public webApiClient?: WebApiClient;

    private _loginDialog?: IPage;

    // #endregion

    // #region Properties

    public authenticationScheme!: PropertyString;
    public configuration!: Property<Configuration>;
    public firstMenuItemIsHomePage!: PropertyBoolean;
    public inhibitNetworkErrors!: PropertyBoolean;
    public loginWithDialog!: PropertyBoolean;
    public supportDesktopLayout!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-application");

        if (Application.instance)
            throw new Error("Application has already been instantiated!");

        Application.instance = this;

        const
            applicationData = new ApplicationData(change => this._authenticatedChanged(change)),
            applicationInformation = ServiceManager.getRequired(ApplicationInformationService),
            windowAsAny = (window as any);

        ServiceManager.add(ApplicationService, Application.instance);
        ServiceManager.add(ApplicationDataService, applicationData);

        if (applicationInformation.isDevelopment)
        {
            windowAsAny.SeriouslyRAD = windowAsAny.SeriouslyRAD ?? {};
            windowAsAny.SeriouslyRAD.application = this;
            windowAsAny.SeriouslyRAD.applicationData = applicationData;
        }

        $$("html").radControl(this);

        this.checkLoginTimeoutId = 0;
        this.loadedInitialPage = false;
        this.onAuthenticatedAsyncCalled = false;
        this.triggeredReady = false;

        applicationData.authenticationData.set(
            applicationInformation.authenticationData
                ? ObjectHelper.fromJson(window.atob(applicationInformation.authenticationData ?? StringHelper.empty))
                : { scheme: this.authenticationScheme.get() }
        );

        this._preInitializationAsync()
            .then(() =>
            {
                this._setDevice();
                this._bindEvents();
                this._setupWebClients();
                this._setupPermissionCache();
                this._setupPageManager();

                if (applicationInformation.isDevelopment)
                    windowAsAny.SeriouslyRAD.pageManager = PageManager.instance;

                this._setupUserAccountDialog();
                this._setupUserMenu();

                this._triggerReady();

                return this._postInitializationAsync();
            });

        // Trigger Property Changes
        this.loginWithDialog.triggerChange();
        this.supportDesktopLayout.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public getPlatform(): string
    {
        return radWindow.device?.platform?.toLowerCase() || "browser";
    }
    public async logoutAsync(): Promise<void>
    {
        ServiceManager.getOptional(ApplicationDataService)?.authenticationData.set(null);

        Assert.isNotNullOrUndefined(this.webApiClient, "this.webApiClient");

        await this.webApiClient
            .createApiMethodRequest("Security", "Logout", null, null, undefined, true)
            .executeAsync();

        this._checkLoginAsync();
    }

    // #endregion

    // #region Protected Methods

    protected _authenticatedChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
        {
            $$("html").addClass("rad-state-authenticated");
            $$("html").removeClass("rad-state-unauthenticated");
        }
        else
        {
            $$("html").removeClass("rad-state-authenticated");
            $$("html").addClass("rad-state-unauthenticated");
        }
    }
    protected _authenticationSchemeChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _bindEvents()
    {
        $$(document).on("SeriouslyRAD.NotificationHub:UserSettingsChanged", { self: this }, this.onUserSettingsChanged);
        $$(document).on("SeriouslyRAD.AjaxRequest:ResponseMessagesReceived", { self: this }, this.onAjaxResponseMessagesReceived);
        $$(document).on("SeriouslyRAD.Application:Authenticated", { self: this }, this.onAuthenticated);
        $$(document).on("SeriouslyRAD.Application:Unauthenticated", { self: this }, this.onUnauthenticated);
        $$(document).on("SeriouslyRAD.PageManager:Navigated", { self: this }, this.onPageManagerNavigated);
    }
    protected _checkLoginAsync()
    {
        return new Promise((resolve, reject) =>
        {
            const
                showLoading = !ServiceManager.getRequired(ApplicationDataService).authenticated.get(),
                headers = this._createAuthenticationHeaders();

            Assert.isNotNullOrUndefined(this.webApiClient, "this.webApiClient");

            this.webApiClient
                .createApiMethodRequest("Security", "CheckLogin", null, headers, showLoading, true)
                .executeAsync()
                .then(data =>
                {
                    if (this.onAuthenticatedAsyncCalled)
                        return;

                    return this._onAuthenticatedAsync(data);
                })
                .then(() =>
                {
                    resolve(undefined);
                })
                .catch(error =>
                {
                    this._onUnauthenticatedAsync()
                        .finally(() =>
                        {
                            reject(error);
                        });
                });
        });
    }
    public _createAuthenticationHeaders()
    {
        const
            headers: any = {},
            authenticationData = ServiceManager.getRequired(ApplicationDataService).authenticationData.get();

        if (authenticationData)
        {
            switch (authenticationData.scheme)
            {
                case "Bearer":
                    if (authenticationData.accessToken)
                        headers["Authorization"] = `${authenticationData.scheme} ${authenticationData.accessToken}`;
                    break;
                case "Cookies":
                    if (authenticationData.antiForgeryToken)
                        headers["X-XSRF-Token"] = authenticationData.antiForgeryToken;
                    break;
            }
        }

        return headers;
    }
    protected _getInitialPageUrl()
    {
        if (this.loadedInitialPage)
            return null;

        this.loadedInitialPage = true;

        const
            location = PathHelper.getLocation(window.location);

        if ((location.applicationRelativeUrl !== StringHelper.empty && location.applicationRelativeUrl !== "index.html")) // index.html = Cordova
            return null;

        if (!this.firstMenuItemIsHomePage.get())
            return null;

        const
            $$mainMenu = this._mainMenuControlRef.current?.$$element.get();

        if (!$$mainMenu)
            return null;

        const
            firstMainMenuButton =
                $$mainMenu
                    .find(".rad-control-accordion-panel:not(.rad-state-hidden) .rad-control-button:not(.rad-state-hidden)")
                    .first()
                    .radControl() as Button | undefined;

        return firstMainMenuButton?.href.get() ?? null;
    }
    protected _inhibitNetworkErrorsChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _loadScriptAsync(src: string)
    {
        const
            promise = PromiseHelper.createPromisePublic();

        if ($$("head script[src='" + src + "']").length > 0)
            promise.resolve();
        else
        {
            const
                element = document.createElement("script");

            element.setAttribute("type", "text/javascript");

            $$("head").append(element);

            element.onload = promise.resolve as any;
            element.onerror = promise.reject;

            element.setAttribute("src", src);
        }

        return promise;
    }
    protected _loadStyleSheetAsync(src: string)
    {
        const
            promise = PromiseHelper.createPromisePublic();

        if ($$("head link[href='" + src + "']").length > 0)
            promise.resolve();
        else
        {
            const
                element = document.createElement('link');

            element.setAttribute("type", "text/css");
            element.setAttribute("rel", "stylesheet");

            $$("head").append(element);

            element.onload = promise.resolve as any;
            element.onerror = promise.reject;

            element.setAttribute("href", src);
        }

        return promise;
    }
    protected _loadUserSettings()
    {
        /* Example
         *

        this.webApiClient
            .createEntitySearchRequest("UserSettings", { filter: "userSettingType/name eq 'Fix Footer'" })
            .executeAsync()
            .then(data =>
            {

            });

        */
    }
    protected _loginWithDialogChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
        {
            $$("html").removeClass("rad-feature-login-page");
            $$("html").addClass("rad-feature-login-dialog");
        }
        else
        {
            $$("html").addClass("rad-feature-login-page");
            $$("html").removeClass("rad-feature-login-dialog");
        }
    }
    protected _openRelevantMainMenuAccordion()
    {
        let
            applicationRelativeUrl = PathHelper.getLocation(window.location).applicationRelativeUrl;

        if (applicationRelativeUrl === null)
            return;

        if (!applicationRelativeUrl.endsWith("/"))
            applicationRelativeUrl += "/";

        if (!applicationRelativeUrl.startsWith("/"))
            applicationRelativeUrl = "/" + applicationRelativeUrl;

        const
            $$mainMenu = this._mainMenuControlRef.current?.$$element.get();

        if (!$$mainMenu)
            return;

        const
            $$matchingButton =
                $$(
                    $$mainMenu
                        .find(".rad-control-accordion-panel:not(.rad-state-hidden) .rad-control-button:not(.rad-state-hidden)")
                        .toArray()
                        .filter(item => !!(item as HTMLAnchorElement).href && (item as HTMLAnchorElement).href.startsWith(window.location.href))
                        .sort(item => item.href.length)
                        .find(item => !!item)
                ),
            accordionPanel =
                $$matchingButton
                    ?.closest(".rad-control-accordion-panel")
                    .radControl() as AccordionPanel | undefined;

        accordionPanel?.open();
    }
    protected _onAuthenticatedAsync(data: any): RadPromiseLike<any>
    {
        if (ServiceManager.getRequired(ApplicationDataService).authenticated.get() === true)
            return Promise.resolve();

        if (this._onAuthenticatedAsyncPromise)
            return this._onAuthenticatedAsyncPromise;

        this._onAuthenticatedAsyncPromise = PromiseHelper.createPromisePublic();

        radWindow.fullPageLoader?.enqueue();

        this.permissionCache?.refresh()
            .then(() =>
            {
                this._startCheckLogin();
                this._loadUserSettings();
                this._triggerAuthenticated();

                const
                    initialPageUrl = this._getInitialPageUrl();

                if (initialPageUrl)
                    PageManager.instance?.navigateAsync(initialPageUrl, true);
                else if (this.loginWithDialog.get())
                    PageManager.instance?.reloadCurrentPageAsync();

                this._openRelevantMainMenuAccordion();

                this._onAuthenticatedAsyncPromise?.resolve();
                delete this._onAuthenticatedAsyncPromise;
            })
            .catch(() =>
            {
                this._onUnauthenticatedAsync()
                    .finally(() =>
                    {
                        this._onAuthenticatedAsyncPromise?.reject();
                        delete this._onAuthenticatedAsyncPromise;
                    });
            })
            .finally(() =>
            {
                radWindow.fullPageLoader?.dequeue();
            });

        this.onAuthenticatedAsyncCalled = true;

        return this._onAuthenticatedAsyncPromise;
    }
    protected _onRequestAuthenticationData()
    {
        return ServiceManager.getRequired(ApplicationDataService).authenticationData.get();
    }
    protected _onUnauthenticatedAsync(): Promise<any>
    {
        if (ServiceManager.getRequired(ApplicationDataService).authenticated.get() === false)
            return Promise.resolve();

        ServiceManager.getRequired(ApplicationDataService).authenticated.set(false);

        this._stopCheckLogin();

        if (PageManager.instance)
        {
            PageManager.instance.clear();
            PageManager.instance._blockNavigationFunctions = [];
            PageDimmer.closeAllDialogsAsync();
        }

        this.permissionCache?.clear();

        this._triggerUnauthenticated();
        this.onAuthenticatedAsyncCalled = false;

        const
            authenticationData = ServiceManager.getRequired(ApplicationDataService).authenticationData.get();

        if (authenticationData?.scheme === "Bearer" && authenticationData.accessToken && authenticationData.refreshToken)
        {
            return new Promise((resolve, reject) =>
            {
                JNativeStatic
                    .ajax({
                        type: "POST",
                        headers: ObjectHelper.extend(true, {}, WebApiClient.staticHeaders),
                        dataType: "json",
                        contentType: "application/json",
                        url: StringHelper.format("{0}api/Security/{1}", ServiceManager.getRequired(ApplicationInformationService).serverVirtualDirectory, "RefreshToken"),
                        data: ObjectHelper.toJson({
                            accessToken: authenticationData.accessToken,
                            refreshToken: authenticationData.refreshToken
                        }),
                        xhrFields: {
                            withCredentials: true
                        }
                    })
                    .then(results =>
                    {
                        const
                            data = results.response;

                        authenticationData.accessToken = data?.accessToken;
                        authenticationData.refreshToken = data?.refreshToken;

                        // Need to revisit this
                        AjaxRequest.unauthenticated = false;

                        AjaxRequest.processCallQueue();

                        return this._onAuthenticatedAsync(data);
                    })
                    .then(() => { resolve(undefined); })
                    .catch(_ =>
                    {
                        this._requestLoginAsync()
                            .then(() => resolve(undefined))
                            .catch(() => reject());
                    })
            });
        }

        return this._requestLoginAsync();
    }
    protected _postInitializationAsync(): RadPromiseLike<any>
    {
        return Promise.resolve();
    }
    protected _preInitializationAsync(): RadPromiseLike<any>
    {
        return Promise.resolve();
    }
    protected _requestLoginAsync()
    {
        const
            promise = new Promise((resolve, reject) =>
            {
                if (this.loginWithDialog.get())
                {
                    (
                        this._loginDialog
                            ? Promise.resolve(this._loginDialog)
                            : PageManager.instance?.getPageAsDialogAsync("/login/")
                    )
                        .then((page: IPage) =>
                        {
                            this._loginDialog = page;
                            return page.openAsDialogAsync();
                        })
                        .then(result =>
                        {
                            if (result.dialogResult !== DialogResultEnum.ok)
                            {
                                reject();
                                return;
                            }

                            resolve(result.promiseData);
                        })
                        .catch(error =>
                        {
                            reject(error);
                        });
                }
                else
                {
                    setTimeout(() =>
                    {
                        PageManager.instance?.clearHistoryAsync()
                            .finally(() =>
                            {
                                PageManager.instance?.navigateAsync("/login/", true, false, null);
                            });
                    });
                }
            });

        return (
            promise
                .then(data =>
                {
                    AjaxRequest.processCallQueue(ServiceManager.getRequired(ApplicationDataService).authenticationData.get()?.antiForgeryToken);
                    return this._onAuthenticatedAsync(data);
                })
        );
    }
    protected _setDevice()
    {
        $$("html").addClass("rad-device-" + this.getPlatform());
    }
    protected _setupPageManager()
    {
        PageManager.instance = new PageManager({
            configuration: this.configuration.get()?.pageManager,
            onAuthenticatedAsync: (data: any) => this._onAuthenticatedAsync(data),
            onRequestAuthenticationData: () => this._onRequestAuthenticationData(),
            onUnauthenticatedAsync: () => this._onUnauthenticatedAsync(),
        });
    }
    protected _setupPermissionCache()
    {
        this.permissionCache = new PermissionCache({ webApiClient: this.webApiClient });

        ServiceManager.add(PermissionCacheService, this.permissionCache);
        (window as any).permissionCache = this.permissionCache;
    }
    protected _setupUserAccountDialog()
    {
        this.userAccountDialog = new UserAccountDialog();
    }
    protected _setupUserMenu()
    {
        this.userMenu =
            new Menu({ text: "User Menu" })
                .append(new MenuItem({ text: "My Account" }))
                .append(new MenuItem({ text: "Logout" }));

        $$(document).on("click", ".rad-action-user-menu-open", (evt: IJNativeEvent) =>
        {
            this.userMenu?.openAsync()
                .then(menuItem =>
                {
                    if (!menuItem)
                        return;

                    switch (menuItem.text.get())
                    {
                        case "My Account":
                            this.userAccountDialog?.openAsync();
                            break;
                        case "Logout":
                            this.logoutAsync();
                            break;
                    }
                });
        });
    }
    protected _setupWebClients()
    {
        const
            applicationInformation = ServiceManager.getRequired(ApplicationInformationService);

        this.webClient = new WebClient({
            baseUrl: applicationInformation.virtualDirectory,
            onRequestAuthenticationData: () => this._onRequestAuthenticationData()
        });

        this.webApiClient = new WebApiClient({
            baseUrl: applicationInformation.serverVirtualDirectory,
            inhibitNetworkErrors: this.inhibitNetworkErrors.get(),
            onAuthenticatedAsync: (data) => this._onAuthenticatedAsync(data),
            onConnected: () => this._triggerConnected(),
            onDisconnected: () => this._triggerDisconnected(),
            onRequestAuthenticationData: () => this._onRequestAuthenticationData(),
            onUnauthenticated: () => this._onUnauthenticatedAsync()
        });

        ServiceManager.add(WebClientService, this.webClient);
        ServiceManager.add(WebApiClientService, this.webApiClient);
    }
    protected _startCheckLogin()
    {
        this._stopCheckLogin();

        this.checkLoginTimeoutId =
            window.setTimeout(async () =>
            {
                try
                {
                    await this._checkLoginAsync();
                    this._startCheckLogin();
                }
                catch { /* Intentionally empty */ }

            }, 30 * 1000);
    }
    protected _stopCheckLogin()
    {
        if (!this.checkLoginTimeoutId)
            return;

        clearTimeout(this.checkLoginTimeoutId);
        delete this.checkLoginTimeoutId;
    }
    protected _supportDesktopLayoutChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            $$("html").addClass("rad-feature-support-desktop-layout");
        else
            $$("html").removeClass("rad-feature-support-desktop-layout");
    }
    protected _triggerAuthenticated()
    {
        $$(document).trigger("SeriouslyRAD.Application:Authenticated", this);
    }
    protected _triggerConnected()
    {
        $$(document).trigger("SeriouslyRAD.Application:Connected", this);
    }
    protected _triggerDisconnected()
    {
        $$(document).trigger("SeriouslyRAD.Application:Disconnected", this);
    }
    protected _triggerReady()
    {
        if (this.triggeredReady)
            return;

        this.triggeredReady = true;
        $$(document).trigger("SeriouslyRAD.Application:Ready", this);
    }
    protected _triggerUnauthenticated()
    {
        $$(document).trigger("SeriouslyRAD.Application:Unauthenticated", this);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._mainMenuControlRef = new ControlRef();
        this._panelDialogContainerControlRef = new ControlRef();
        this._panelPageContainerControlRef = new ControlRef();

        this.authenticationScheme = new PropertyString(this, propertyValues.authenticationScheme, this._authenticationSchemeChanged);
        this.configuration = new Property<Configuration>(this, "System.Object, System.Private.CoreLib", propertyValues.configuration);
        this.firstMenuItemIsHomePage = new PropertyBoolean(this, propertyValues.firstMenuItemIsHomePage);
        this.inhibitNetworkErrors = new PropertyBoolean(this, propertyValues.inhibitNetworkErrors, this._inhibitNetworkErrorsChanged);
        this.loginWithDialog = new PropertyBoolean(this, propertyValues.loginWithDialog, this._loginWithDialogChanged);
        this.supportDesktopLayout = new PropertyBoolean(this, propertyValues.supportDesktopLayout, this._supportDesktopLayoutChanged);
    }

    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <div>
                <WindowHeader>
                    <Toolbar colorInverted={true}>
                        <ToolbarButton icon="content" cssClass="rad-action-main-menu-open" />
                        <ToolbarImage cssClass="rad-action-header-logo-click" />
                        <ToolbarSplitter />
                        <ToolbarButton icon="user" iconInline={true} showLabel={true} cssClass="rad-action-user-menu-open" />
                    </Toolbar>
                </WindowHeader>
                <MainMenu ref={this._mainMenuControlRef}>
                    <Accordion>
                    </Accordion>
                </MainMenu>
                <Panel ref={this._panelPageContainerControlRef} cssClass="rad-page-container pusher" elementTypeOverride="main" />
                <Panel ref={this._panelDialogContainerControlRef} cssClass="rad-dialog-container" />
            </div>
        );
    }
    protected override _createElementComplete()
    {
        super._createElementComplete();
        this._mainMenuControlRef.current?.start(this.$$element.get()?.get(0));
    }

    // #endregion

    // #region Event Handlers

    protected onAjaxResponseMessagesReceived(evt: IJNativeEvent, eventData: any)
    {
        AjaxResponseMessageDialog.displayMessages(eventData.responseMessages);
    }
    protected onAuthenticated(evt: IJNativeEvent)
    {
        /* Intentionally empty */
    }
    protected onPageManagerNavigated(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (self.initialized)
            return;

        $$("html").removeClass("rad-state-initializing");

        self.initialized = true;
    }
    protected onUnauthenticated(evt: IJNativeEvent)
    {
        /* Intentionally empty */
    }
    protected onUserSettingsChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._loadUserSettings();
    }

    // #endregion

    // #endregion
}